import React, {useState, useEffect} from 'react';
import { Box, Grid2, Typography, Button, LinearProgress } from '@mui/material';
import TarFileUpload from './components/tarFileUpload';
import ValidDocumentFrontImage from './components/validDocumentFrontImage';
import ValidDocumentBackImage from './components/validDocumentBackImage';
import FraudDocumentFrontImage from './components/fraudDocumentFrontImage';
import FraudDocumentBackImage from './components/fraudDocumentBackImage';
import Resumable from 'resumablejs';
import { useNavigate } from 'react-router-dom';
import { toast, ToastContainer, Slide } from 'react-toastify';
import byteConversion from './components/byteConversion';
import config from './config';
import "react-toastify/dist/ReactToastify.css";
import "./toast.css"

export default function DocumentValidation({isAdmin, isSubmit}) {
    const [overallProgress, setOverallProgress] = useState(0);
    const [isSubmitEnabled, setIsSubmitEnabled] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const [isSubmitted, setIsSubmitted] = useState(false);

    const navigate = useNavigate();

     // maps upload file keys to file objects
    const [files, setFiles] = useState({
        dockerImage: null,
        validFrontImage: null,
        validBackImage: null,
        fraudFrontImage: null,
        fraudBackImage: null,
    });

    const fileNameOverrides = {
        dockerImage: null,
        validFrontImage: 'validDocumentFrontImage',
        validBackImage: 'validDocumentBackImage',
        fraudFrontImage: 'fraudDocumentFrontImage',
        fraudBackImage: 'fraudDocumentBackImage',
    };

    const toastOptions = {
        position: "bottom-center",
        autoClose: 8000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "colored",
        transition: Slide
    }
    
    const receiveDataset = (files, type) => {
        onFileChange(files[0], type);
    }

    const onFileChange = (incomingFile, key) => {
        const file = Object.defineProperty(incomingFile, 'key', { value: key});
        if (!file) {
            toast.error(`Error: unable to upload file "${file.name}"`, toastOptions);
            setIsSubmitEnabled(false);
            return;
        }
        if (file.name.slice(-2) === 'gz' && file.size > config.MAX_FILE_SIZE_BYTES_DOCKER) {
            toast.error(`Error: file "${file.name}" is larger than maximum allowed size`, toastOptions);
            return;
        }
        if (file.name.slice(-2) !== 'gz' && file.size > config.MAX_FILE_SIZE_BYTES_MEDIA) {
            toast.error(`Error: file "${file.name}" is larger than maximum allowed size`, toastOptions);
            return;
        }
        const validFileNameRegex = /^[a-zA-Z0-9._-]+$/;
        if (!validFileNameRegex.test(file.name)) {
            setIsSubmitEnabled(false);
            toast.error(`Error: file name "${file.name}" has invalid characters, can only contain letters, numbers, . _ or -`, toastOptions);
            return;
        }
        const fileNameExists = Object.values(files).some(f => f && f.name === file.name);
        if (fileNameExists) {
            toast.error(`Error: uploaded file name "${file.name}" must be unique, existing file ${file.name} already exists`, toastOptions);
            setIsSubmitEnabled(false);
            return;
        }
        file.uploadKey = key;
        updateFile(file);
    };

    const updateFile = (file) => {
        const key = file.key;
        setFiles(prevFiles => {
            const fileExists = Object.values(prevFiles).some(f => f && f.key === key);

            if(fileExists) {
                const fileKey = Object.entries(prevFiles)
                    .filter(([fileKey, f]) => f && f.key === key)
                    .map(([fileKey]) => fileKey);

                return {...prevFiles, [fileKey]: file}
            } else {
                if (key === 'dockerImage') {
                    return {...prevFiles, dockerImage: file}
                } else if (key === 'validDocumentFrontImage') {
                    return {...prevFiles, validFrontImage: file}
                } else if (key === 'validDocumentBackImage') {
                    return {...prevFiles, validBackImage: file}
                } else if (key === 'fraudDocumentFrontImage') {
                    return {...prevFiles, fraudFrontImage: file}
                } else {
                    return {...prevFiles, fraudBackImage: file}
                }
            }
        })
    }

    const resumable = new Resumable({
        target: `${config.SERVER_HOST}/upload`,
        query: { uploadType: "track1" }, // we will also add submission timestamp when submitted
        fileType: ['tgz', 'tar.gz', 'jpg', 'png'],
        maxFileSize: config.MAX_FILE_SIZE_BYTES_DOCKER,
        testChunks: false,
        chunkSize: config.MAX_FILE_SIZE_BYTES_DOCKER,
        withCredentials: true
    });

    resumable.on('fileAdded', function (file) {
        resumable.upload();
    });

    resumable.on('fileProgress', function (file) {
        if (!file) {
            return;
        }

        let totalProgress = 0;
        const totalFiles = resumable.files.length;

        resumable.files.forEach((file) => {
            totalProgress += file.progress();
        });

        const averageProgress = (totalProgress / totalFiles) * 100;
        setOverallProgress(averageProgress);
    });

    resumable.on('fileSuccess', function (file, message) {
        if (!file) {
            return;
        }
        toast.dismiss();
        if (resumable.files.every((file) => file.isComplete())) {
            setIsUploading(false);
            setIsSubmitted(true);
            isSubmit(false);
            navigate(isAdmin ? "/admin": "/home", {state: {formSubmitted: true}});
        }
    });

    resumable.on('fileError', function (file, message) {
        if (!file) {
            return;
        }
        toast.dismiss();
        setIsUploading(false);
        setIsSubmitEnabled(true);
        isSubmit(false);
        toast.error("Error, submission not complete, file error, please try again", toastOptions);
    });

    const onSubmit = () => {
        isSubmit(true);
        toast.dismiss();
        setIsSubmitEnabled(false);
        setIsUploading(true);
        setIsSubmitted(true);
        resumable.opts.query.submissionTimestamp = new Date().toISOString(); // add timestamp to query
        Object.values(files).forEach((file, index) => {
            resumable.addFile(file);
            if (!resumable.files[index]) {
                resumable.cancel();
                setIsSubmitEnabled(true);
                setIsUploading(false);
                setIsSubmitted(false);
                toast.dismiss();
                toast.error("Error, submission not complete, invaild file, please try again", toastOptions);
                return;
            }
            // map images to fixed file names
            if (fileNameOverrides[file.uploadKey]) {
                const fileEnding = file.type.split('/')[1];
                const useEnding = fileEnding === 'jpeg' ? 'jpg' : 'jpg';
                const fileName = `${fileNameOverrides[file.uploadKey]}.${useEnding}`;
                resumable.files[index].fileName = fileName;
            }
        });
    };

    useEffect(() => {
        const areAllFilesSelected = Object.keys(files).every(key => files[key] !== null);
        setIsSubmitEnabled(areAllFilesSelected);
      }, [files]);

    return(
        <Box className='container-box'>
            <Box className='page-box' >
                <Typography color="fontTheme" 
                            className='page-header'>Document Validation Submission</Typography>
                <Typography className='page-description'>
                    In order to successfully submit your docker container for consideration you are required to upload an algorithm docker image tgz file implementing 
                    the MdTF Document Validation API, with a sample “Valid Document Front Image”, a sample “Valid Document Back Image”, 
                    a sample “Fraudulent Document Front Image”, and a sample “Fraudulent Document Back Image”.
                </Typography>
            </Box>
            <Box className='below-content'>
               {(!isSubmitted && !isUploading) &&
                <Grid2 container columnSpacing={2}>
                    <Grid2  size={{xs: 12}} sx={{width: '18.5%'}}>
                        <Typography className='page-description' sx={{width: "99%"}}>
                            <b>Step 1:</b> Upload File: Docker Container Image
                        </Typography>
                            <form onSubmit={onSubmit}>
                                <TarFileUpload fileUpload={receiveDataset}/>
                            </form>
                        <Typography className='file-name file-name-small'>
                            {files.dockerImage !== null 
                            ? <> <b>{files.dockerImage.path.replace('./', '')} ({byteConversion(files.dockerImage.size)})</b> </> 
                            : ''}
                        </Typography>
                        
                    </Grid2>
                    <Grid2  size={{xs: 12}} sx={{width: '18.5%'}}>
                        <Typography className='page-description' sx={{width: "99%"}}>
                            <b>Step 2:</b> Upload File: Valid Document Front Image - Sample
                        </Typography>
                            <form onSubmit={onSubmit}>
                                <ValidDocumentFrontImage fileUpload={receiveDataset}/>
                            </form>
                        <Typography className='file-name file-name-small'>
                            {files.validFrontImage !== null 
                            ? <> <b>{files.validFrontImage.path.replace('./', '')} ({byteConversion(files.validFrontImage.size)})</b> </>  
                            : ''}
                       </Typography>
                    </Grid2>
                    <Grid2  size={{xs: 12}} sx={{width: '18.5%'}}>
                        <Typography className='page-description' sx={{width: "99%"}}>
                            <b>Step 3:</b> Upload File: Valid Document Back Image - Sample
                        </Typography>
                            <form onSubmit={onSubmit}>
                                <ValidDocumentBackImage fileUpload={receiveDataset}/>
                            </form>
                        <Typography className='file-name  file-name-small'>
                            {files.validBackImage !== null 
                            ? <> <b>{files.validBackImage.path.replace('./', '')} ({byteConversion(files.validBackImage.size)})</b> </> 
                            : ''}
                        </Typography>
                    </Grid2>
                    <Grid2  size={{xs: 12}} sx={{width: '18.5%'}}>
                        <Typography className='page-description' sx={{width: "99%"}}>
                            <b>Step 4:</b> Upload File: Fraudulent Document Front Image - Sample
                        </Typography>
                            <form onSubmit={onSubmit}>
                                <FraudDocumentFrontImage fileUpload={receiveDataset}/>
                            </form>
                        <Typography className='file-name  file-name-small'>
                            {files.fraudFrontImage !== null 
                            ? <> <b>{files.fraudFrontImage.path.replace('./', '')}</b> ({byteConversion(files.fraudFrontImage.size)}) </> 
                            : ''}
                        </Typography>
                    </Grid2>
                    <Grid2  size={{xs: 12}} sx={{width: '18.5%'}}>
                        <Typography className='page-description' sx={{width: "99%"}}>
                            <b>Step 5:</b> Upload File: Fraudulent Document Back Image - Sample
                        </Typography>
                            <form onSubmit={onSubmit}>
                                <FraudDocumentBackImage fileUpload={receiveDataset}/>
                            </form>
                        <Typography className='file-name  file-name-small'>
                            {files.fraudBackImage !== null 
                            ? <> <b>{files.fraudBackImage.path.replace('./', '')}</b> ({byteConversion(files.fraudBackImage.size)}) </> 
                            : ''}
                        </Typography>
                    </Grid2>
                    <Box sx={{width: '100%'}} />
                    <Grid2  size={{xs: 12}} sx={{width: '18.5%'}}>
                        <Typography className='file-name file-name-large'>
                            {files.dockerImage !== null 
                            ? <> <b>{files.dockerImage.path.replace('./', '').toString()}</b> ({byteConversion(files.dockerImage.size)}) </> 
                            : ''}
                        </Typography>
                    </Grid2>
                    <Grid2  size={{xs: 12}} sx={{width: '18.5%'}}>
                        <Typography className='file-name file-name-large'>
                            {files.validFrontImage !== null 
                            ? <> <b>{files.validFrontImage.path.replace('./', '')}</b> ({byteConversion(files.validFrontImage.size)}) </>  
                            : ''}
                       </Typography>
                    </Grid2>
                    <Grid2  size={{xs: 12}} sx={{width: '18.5%'}}>
                        <Typography className='file-name file-name-large'>
                            {files.validBackImage !== null 
                            ? <> <b>{files.validBackImage.path.replace('./', '')}</b> ({byteConversion(files.validBackImage.size)}) </> 
                            : ''}
                        </Typography>
                    </Grid2>
                    <Grid2  size={{xs: 12}} sx={{width: '18.5%'}}>
                        <Typography className='file-name file-name-large'>
                            {files.fraudFrontImage !== null 
                            ? <> <b>{files.fraudFrontImage.path.replace('./', '')}</b> ({byteConversion(files.fraudFrontImage.size)}) </> 
                            : ''}
                        </Typography>
                    </Grid2>
                    <Grid2  size={{xs: 12}} sx={{width: '18.5%'}}>
                        <Typography className='file-name file-name-large'>
                            {files.fraudBackImage !== null 
                            ? <> <b>{files.fraudBackImage.path.replace('./', '')}</b> ({byteConversion(files.fraudBackImage.size)}) </> 
                            : ''}
                        </Typography>
                    </Grid2>
                    <Grid2  size={{xs: 12, lg: 12}} sx={{marginTop: '3%'}}>
                        <Box sx={{float: 'left'}}>
                            <Typography className='page-description'>
                                Select <b>SUBMIT DOCKER</b> to complete your submission.
                            </Typography>
                        </Box>
                    </Grid2>
                    <Grid2  size={{xs: 12, lg: 12}} sx={{marginTop: '4%'}}>
                        <Box sx={{float: 'right'}}>
                            <Button className='submit-buttons' onClick={onSubmit} disabled={!isSubmitEnabled} variant='ouline'>SUBMIT DOCKER</Button>
                        </Box>
                    </Grid2>
                </Grid2>
                }
                {isUploading &&
                    <>
                        <Box sx={{display: 'flex', justifyContent: 'center', height: '344px'}}>      
                            <Box sx={{width: '75%', marginRight: '10px'}}>     
                            <LinearProgress variant="determinate" 
                                            value={overallProgress}
                                            color="fontTheme"
                                            sx={{height: '30px'}}/>
                            </Box>
                            <Box>
                                <Typography color='fontTheme' variant='h6'>
                                    {parseInt(overallProgress)}%
                                </Typography>
                            </Box> 
                        </Box>
                    </>
                }
                <ToastContainer />
            </Box>
        </Box>
    )
}
